import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Divider,
  Spin,
  Switch,
  Button,
  Table,
  Input,
  InputNumber,
  Popconfirm,
  Select,
  Radio,
  message,
} from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBuilding,
  faGears,
  faCalendar,
  faTrash,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
// COMPONENTS
import FormInputText from "../../components/form/FormInputText";
import FormInputPhoneNumber from "../../components/form/FormInputPhoneNumber";
import FormInputSelectCountry from "../../components/form/FormInputSelectCountry";
import FormInputSelectState from "../../components/form/FormInputSelectState";
import FormInputSelectCity from "../../components/form/FormInputSelectCity";
import DrawerRowDivider from "../../components/drawer/DrawerRowDivider";
import GlobalAlert2 from "../../home/GlobalAlert2";
import SelectOrganizationsLight from "../../components/select/SelectOrganizationsLight";
// CUSTOM HOOKS
import useGetAllOrganizationsLight from "../../../customHooks/GET_requests/useGetAllOrganizationsLight";
// REDUX
import {
  usePostOrganizationMutation,
  usePutOrganizationMutation,
  usePatchOrganizationSettingsMutation,
  usePostHolidayMutation,
  useDeleteHolidayMutation,
  useLazyGetHolidaysQuery,
  useLazyGetOrganizationAndChildsQuery,
  useDeleteOrganizationMutation,
} from "../../../redux/organizations/organizationAPI";
// AUTH
import GETJwtToken from "../../../redux/authentication/authentication";
// INTERFACES
import {
  OganizationLightInterface,
  OrganizationInterface,
} from "../../../interfaces/Organizations.interface";
// CONSTANTS
import { BLANK_ORGANIZATION } from "../../../constants/organization";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";
// ACTION FIRING MODE
import { ActionFiringMode } from "../../../flatbuffers/generated/archive/action-firing-mode";
import { toActionFiringMode } from "../../../utils/actionFiringMode";
import { OrgType } from "../../../flatbuffers/generated/archive/org-type";
// CONTEXT
import { useWizard } from "../WizardContext";
// UTILS
import collectSubtreeIds from "../utils/orgSubtree";
// STORE
import { RootState } from "../../../app/store";
import WizardExportButton from "../components/WizardExportButton";
import { WizardOpGate, useCurrentWizardStepAccess } from "../WizardStepGate";

interface HolidayRow {
  key: string;
  id?: string;
  name: string;
  description: string;
  day: number;
  month: number;
}

const COMMON_HOLIDAYS: Omit<HolidayRow, "key" | "id">[] = [
  { name: "New Year's Day", description: "", day: 1, month: 1 },
  {
    name: "Martin Luther King Jr. Day",
    description: "Third Monday in January",
    day: 20,
    month: 1,
  },
  {
    name: "Presidents' Day",
    description: "Third Monday in February",
    day: 17,
    month: 2,
  },
  {
    name: "Memorial Day",
    description: "Last Monday in May",
    day: 26,
    month: 5,
  },
  { name: "Independence Day", description: "", day: 4, month: 7 },
  {
    name: "Labor Day",
    description: "First Monday in September",
    day: 1,
    month: 9,
  },
  {
    name: "Columbus Day",
    description: "Second Monday in October",
    day: 13,
    month: 10,
  },
  { name: "Veterans Day", description: "", day: 11, month: 11 },
  {
    name: "Thanksgiving",
    description: "Fourth Thursday in November",
    day: 27,
    month: 11,
  },
  { name: "Christmas Day", description: "", day: 25, month: 12 },
  { name: "Christmas Eve", description: "", day: 24, month: 12 },
  { name: "New Year's Eve", description: "", day: 31, month: 12 },
];

/** Narrow the interface's OrgType onto the two the radio offers. */
function orgTypeOfValue(value: unknown): "ORG" | "SUBORG" {
  return value === OrgType.SUBORG || value === "SUBORG" ? "SUBORG" : "ORG";
}

/**
 * The organization's own fields, in the shape the update endpoint takes.
 *
 * Module-level and pure so the hydration path and the save path cannot end up
 * with two different notions of "the organization body". One of them decides
 * whether to send a request and the other builds it; if they disagreed, the
 * skip below would either fire when it should not or never fire at all.
 *
 * Key order is fixed by this literal, so JSON.stringify of two equal states is
 * byte-identical and comparing the strings is meaningful.
 */
function orgBodyOf(fields: OrganizationInterface, type: "ORG" | "SUBORG"): Record<string, unknown> {
  return {
    name: fields.name,
    address: fields.address,
    phone: fields.phone,
    phone_code: fields.phoneCode,
    type,
    country: fields.country,
    state: fields.state,
    city: fields.city,
    parent_id: typeof fields.parentId === "string" ? fields.parentId : formatUuidT(fields.parentId),
  };
}

/**
 * The Select value standing for "this organization has not chosen".
 *
 * An empty string rather than "INHERIT": INHERIT is what the SERVER stores for
 * an organization that never chose, but it is not a value a client may SET —
 * LiftCloud's ValidOrgActionFiringMode rejects it, because there is nothing
 * above an organization to inherit from. So the option exists in the UI and is
 * expressed on the wire by omitting the field.
 */
const UNSET_FIRING_MODE = "";

/**
 * The settings PATCH body, with the firing mode omitted when unset.
 *
 * Omitted rather than sent empty: `action_firing_mode` is `omitempty` on the Go
 * side, so an empty string would be indistinguishable from not sending it, and
 * sending a value the server validates would fail. Extracted because there are
 * two save paths — create-then-save and plain save — and the field being added
 * to only one of them is exactly the kind of divergence that ships.
 */
export function orgSettingsBody(
  doorUnknownAsOpening: boolean,
  hideUnconfiguredDoors: boolean,
  actionFiringMode: string,
): Record<string, unknown> {
  const body: Record<string, unknown> = {
    door_unknown_as_opening: doorUnknownAsOpening,
    hide_unconfigured_doors: hideUnconfiguredDoors,
  };

  if (actionFiringMode !== UNSET_FIRING_MODE) {
    body.action_firing_mode = actionFiringMode;
  }

  return body;
}

/**
 * The organization PUT body, with the firing mode included when known.
 *
 * Built on top of orgBodyOf rather than beside it, so there remains exactly
 * one function that knows an organization PUT's core shape. The addition
 * exists because requestSerializers.ts packs a body with no
 * action_firing_mode as TWO_STEP — a full-entity PUT has no way to encode
 * "not sent, leave it alone" — so whenever this PUT fires for an unrelated
 * reason (an address edit, say), its wire body must carry the mode this
 * organization already has, or that edit silently resets it. Same
 * omit-when-unset rule as orgSettingsBody, for the same reason: the server
 * rejects an explicit empty/INHERIT value from an organization.
 */
function orgPutBody(
  fields: OrganizationInterface,
  type: "ORG" | "SUBORG",
  actionFiringMode: string,
): Record<string, unknown> {
  const body = orgBodyOf(fields, type);
  if (actionFiringMode !== UNSET_FIRING_MODE) {
    body.action_firing_mode = actionFiringMode;
  }
  return body;
}

export default function StepOrganization() {
  const [t] = useTranslation("global");
  const { theme } = useSelector((state: RootState) => state.home);
  const { state: wizardState, setState: setWizardState, markStepCompleted } = useWizard();

  const isEditMode = !!wizardState.organizationId;
  const access = useCurrentWizardStepAccess();

  // ************************************************ */
  // LOCAL STATE ************************************ */
  const [organization, setOrganization] = useState<OrganizationInterface>({
    ...BLANK_ORGANIZATION,
  });
  const [orgType, setOrgType] = useState<"ORG" | "SUBORG">("ORG");
  // Defaults to TRUE, and its sibling below has always said so. A door whose
  // state the controller never reports is likelier to be standing open than
  // sealed, and treating the unknown as closed is the direction that hides a
  // real open door on the dashboard. The request serializer already defaults
  // this field to true when the key is absent; this initialiser is what an
  // operator SEES before they touch anything, and it disagreed.
  const [doorUnknownAsOpening, setDoorUnknownAsOpening] = useState(true);
  const [hideUnconfiguredDoors, setHideUnconfiguredDoors] = useState(true);
  /**
   * How this organization's operators dispatch a floor-targeting command.
   *
   * Starts UNSET rather than at a mode, and that is the whole safety property.
   * An organization that has never chosen must keep behaving exactly as it did:
   * the Car Calls pad sends on the first floor-button click, an Actions card
   * stages the floor and waits. Initialising this to a real mode would show an
   * operator a setting their site is not actually using, and — worse — save it
   * the next time they pressed Save for an unrelated reason, changing how
   * elevator commands dispatch as a side effect of editing an address.
   */
  const [actionFiringMode, setActionFiringMode] = useState<string>(UNSET_FIRING_MODE);
  const [holidays, setHolidays] = useState<HolidayRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [stepError, setStepError] = useState<string | null>(null);
  const originalHolidayIdsRef = useRef<Set<string>>(new Set());
  /**
   * The organization's fields as the server last gave them to us.
   *
   * Kept so an update can tell whether the ORG ITSELF changed. Settings and
   * holidays have their own endpoints, and sequencing them behind an
   * unconditional org PUT means an unrelated validation failure on the org
   * takes them all down with it: `.unwrap()` throws, and every await after it
   * is skipped. That is how toggling one boolean became unsavable on an
   * organization whose parent hierarchy the server refuses — the boolean was
   * never the thing being rejected.
   */
  const savedOrgFieldsRef = useRef<string | null>(null);

  // ************************************************ */
  // RTK QUERY HOOKS ******************************* */
  const [
    triggerPostOrganization,
    {
      isSuccess: isSuccessPost,
      isLoading: isLoadingPost,
      isError: isErrorPost,
      error: errorPost,
      data: dataPost,
    },
  ] = usePostOrganizationMutation();

  const [triggerPutOrganization, { isError: isErrorPut, error: errorPut }] =
    usePutOrganizationMutation();

  const [triggerPatchSettings, { isError: isErrorPatch, error: errorPatch }] =
    usePatchOrganizationSettingsMutation();

  const [triggerPostHoliday] = usePostHolidayMutation();
  const [triggerDeleteHoliday] = useDeleteHolidayMutation();

  const [triggerGetOrganization, { data: dataGetOrg, isLoading: isLoadingGetOrg }] =
    useLazyGetOrganizationAndChildsQuery();

  const [triggerGetHolidays, { data: holidaysData }] = useLazyGetHolidaysQuery();

  const [triggerDeleteOrg, { isLoading: isLoadingDelete }] = useDeleteOrganizationMutation();

  const { organizationsLight } = useGetAllOrganizationsLight();

  // Parent-picker exclusions: the org being edited plus its whole subtree, so it
  // cannot be reparented under itself or a descendant (which would create a
  // cycle; the backend rejects this too). Empty in create mode — a new org has
  // no id or descendants yet, so no cycle is possible.
  const parentExcludeIds = useMemo(() => {
    const ids: string[] = [];
    if (wizardState.organizationId) ids.push(wizardState.organizationId);
    ids.push(...collectSubtreeIds(dataGetOrg?.data));
    return ids;
  }, [wizardState.organizationId, dataGetOrg]);

  // Auto-set parentId to the MASTER org when creating an ORG.
  // Every ORG is implicitly a child of the MASTER org.
  useEffect(() => {
    if (orgType === "ORG" && organizationsLight.length > 0) {
      const masterOrg = organizationsLight.find(
        (org: OganizationLightInterface) => org.type === OrgType.MASTER,
      );

      if (masterOrg) {
        setOrganization((prev) => ({ ...prev, parentId: masterOrg.id as any }));
      } else {
        // Fallback: if no type info from API, use the first org
        // (typically the MASTER org is listed first)
        setOrganization((prev) => {
          if (!prev.parentId) {
            return { ...prev, parentId: organizationsLight[0].id as any };
          }

          return prev;
        });
      }
    }
  }, [orgType, organizationsLight]);

  // ************************************************ */
  // FUNCTIONS ************************************* */
  // A functional update, like every other write to `organization` here. Copying the
  // render-time `organization` instead dropped any update queued since that render: when
  // the organization list arrived while the operator was typing, the auto-selected MASTER
  // parent was overwritten with null by the next keystroke, and Save stayed disabled for
  // good because nothing selects the parent again.
  const onChangeFormValue = (newValue: string, variableName: string) => {
    setOrganization((prev) => ({ ...prev, [variableName]: newValue }));
  };

  const onChangeParentOrg = (newValue: string) => {
    setOrganization((prev) => ({ ...prev, parentId: newValue as any }));
  };

  const addHoliday = () => {
    setHolidays((prev) => [
      ...prev,
      {
        key: `holiday-${Date.now()}`,
        name: "",
        description: "",
        day: 1,
        month: 1,
      },
    ]);
  };

  const removeHoliday = (key: string) => {
    setHolidays((prev) => prev.filter((h) => h.key !== key));
  };

  const addCommonHoliday = (index: number) => {
    const preset = COMMON_HOLIDAYS[index];
    const alreadyExists = holidays.some(
      (h) => h.name === preset.name && h.day === preset.day && h.month === preset.month,
    );
    if (alreadyExists) return;
    setHolidays((prev) => [...prev, { ...preset, key: `holiday-${Date.now()}` }]);
  };

  const addAllCommonHolidays = () => {
    const existingNames = new Set(holidays.map((h) => h.name));
    const newHolidays = COMMON_HOLIDAYS.filter((h) => !existingNames.has(h.name)).map((h, i) => ({
      ...h,
      key: `holiday-${Date.now()}-${i}`,
    }));
    if (newHolidays.length > 0) {
      setHolidays((prev) => [...prev, ...newHolidays]);
    }
  };

  const updateHoliday = (key: string, field: string, value: string | number) => {
    setHolidays((prev) => prev.map((h) => (h.key === key ? { ...h, [field]: value } : h)));
  };

  const isFormValid = () =>
    String(organization.name ?? "").trim() !== "" &&
    String(organization.address ?? "").trim() !== "" &&
    String(organization.country ?? "").trim() !== "" &&
    String(organization.state ?? "").trim() !== "" &&
    String(organization.phone ?? "").trim() !== "" &&
    organization.parentId !== null;

  const saveHolidays = async (orgId: string, token: string) => {
    const validHolidays = holidays.filter((h) => h.name.trim());
    if (validHolidays.length === 0) return;
    const results = await Promise.allSettled(
      validHolidays.map((h) =>
        triggerPostHoliday({
          body: {
            name: h.name,
            description: h.description,
            day: h.day,
            month: h.month,
            organization_id: orgId,
          },
          token,
        }).unwrap(),
      ),
    );
    const failCount = results.filter((r) => r.status === "rejected").length;
    if (failCount > 0) {
      setStepError(`${failCount} of ${validHolidays.length} holidays failed to save`);
    }
  };

  const buildOrgBody = (): Record<string, unknown> => orgBodyOf(organization, orgType);

  const handleSave = async () => {
    if (!isFormValid()) return;
    setSaving(true);
    try {
      const token = await GETJwtToken();
      // Same field set as the update path, by construction. Two hand-written
      // copies of an entity's field list is how a field comes to be written on
      // create and silently dropped on update.
      const body: Record<string, unknown> = { ...buildOrgBody(), status: "DRAFT" };
      triggerPostOrganization({ body, token });
    } catch {
      setSaving(false);
    }
  };

  const handleUpdate = async () => {
    if (!isFormValid() || !wizardState.organizationId) return;
    setSaving(true);
    const token = await GETJwtToken();

    try {
      // The change-check baseline. Deliberately excludes action_firing_mode —
      // see orgPutBody — so a settings-only change can still skip this PUT
      // entirely; only orgBodyOf's own fields decide whether the organization
      // itself changed.
      const orgBody = buildOrgBody();

      // Only PUT the organization when the organization actually changed.
      // Settings and holidays are separate endpoints; making them wait on an
      // org write that had nothing to do with them is what let a rejected
      // parent hierarchy block an unrelated toggle for good.
      if (savedOrgFieldsRef.current !== JSON.stringify(orgBody)) {
        await triggerPutOrganization({
          id: wizardState.organizationId,
          body: orgPutBody(organization, orgType, actionFiringMode),
          token,
        }).unwrap();
        savedOrgFieldsRef.current = JSON.stringify(orgBody);
      }

      // Update settings
      await triggerPatchSettings({
        id: wizardState.organizationId,
        body: orgSettingsBody(doorUnknownAsOpening, hideUnconfiguredDoors, actionFiringMode),
        token,
      }).unwrap();

      // Delete removed holidays (existed on backend but user removed)
      const currentBackendIds = new Set(holidays.filter((h) => h.id).map((h) => h.id!));
      const toDelete = [...originalHolidayIdsRef.current].filter(
        (id) => !currentBackendIds.has(id),
      );
      await Promise.all(toDelete.map((id) => triggerDeleteHoliday({ id, token }).unwrap()));

      // Create new holidays (no backend ID)
      const newHolidays = holidays.filter((h) => !h.id && h.name.trim());
      await Promise.all(
        newHolidays.map((h) =>
          triggerPostHoliday({
            body: {
              name: h.name,
              description: h.description,
              day: h.day,
              month: h.month,
              organization_id: wizardState.organizationId,
            },
            token,
          }).unwrap(),
        ),
      );

      // Refresh holidays to get updated IDs
      triggerGetHolidays({ orgId: wizardState.organizationId, token });

      markStepCompleted(0);
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setStepError(`Failed to update organization: ${errMsg}`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrganization = async () => {
    if (!wizardState.organizationId) return;
    const token = await GETJwtToken();
    try {
      await triggerDeleteOrg({
        id: wizardState.organizationId,
        token,
      }).unwrap();
      // Reset entire wizard state
      setWizardState({
        currentStep: 0,
        organizationId: null,
        orgStatus: null,
        serverId: null,
        adapterIds: [],
        channelIds: [],
        bankIds: [],
        bankNames: {},
        bankTypes: {},
        adapterIdByBank: {},
        adapterProtocolByBank: {},
        liftIdsByBank: {},
        liftNamesByBank: {},
        roleIds: [],
        userIds: [],
        pendingUsers: [],
        notificationIds: [],
        actionCatalogIds: [],
        monitorGridId: null,
        completedSteps: [],
        stepInvalidationVersions: {},
        adapterVerified: {},
        adapterValidations: {},
        adapterNames: {},
        hasBacnetConfig: false,
        hasDebounceOverrides: false,
        hasChannelModifiers: false,
      });
      setOrganization({ ...BLANK_ORGANIZATION });
      setOrgType("ORG");
      setDoorUnknownAsOpening(false);
      setHideUnconfiguredDoors(true);
      setHolidays([]);
      originalHolidayIdsRef.current = new Set();
    } catch (err: unknown) {
      const errMsg = err instanceof Error ? err.message : "Unknown error";
      setStepError(`Failed to delete organization: ${errMsg}`);
    }
  };

  // ************************************************ */
  // USE EFFECTS *********************************** */

  // Handle successful initial org creation
  useEffect(() => {
    if (!isSuccessPost || !dataPost) return;
    const orgData = dataPost.data || dataPost;
    const rawOrgId = orgData.id;
    if (!rawOrgId) return;
    // Normalize: FlatBuffer-decoded responses return UuidT objects for id fields.
    // WizardState.organizationId must be a plain string.
    const orgId =
      typeof rawOrgId === "string"
        ? rawOrgId
        : formatUuidT(rawOrgId as Parameters<typeof formatUuidT>[0]);

    const saveAfterCreate = async () => {
      try {
        const token = await GETJwtToken();
        await triggerPatchSettings({
          id: orgId,
          body: orgSettingsBody(doorUnknownAsOpening, hideUnconfiguredDoors, actionFiringMode),
          token,
        }).unwrap();

        await saveHolidays(orgId, token);
      } catch {
        // Errors shown by GlobalAlert2
      }

      setWizardState((prev) => ({
        ...prev,
        organizationId: orgId,
        currentStep: 1,
      }));
      markStepCompleted(0);
      setSaving(false);
    };
    saveAfterCreate();
  }, [isSuccessPost, dataPost]);

  useEffect(() => {
    if (isErrorPost) {
      setSaving(false);
    }
  }, [isErrorPost]);

  // Load org data and holidays when resuming or navigating back
  useEffect(() => {
    if (wizardState.organizationId) {
      const fetchData = async () => {
        const token = await GETJwtToken();
        triggerGetOrganization({
          orgId: wizardState.organizationId,
          token,
        });
        triggerGetHolidays({
          orgId: wizardState.organizationId,
          token,
        });
      };
      fetchData();
    }
  }, [wizardState.organizationId]);

  // Populate form fields once org data is fetched
  useEffect(() => {
    if (dataGetOrg && dataGetOrg.data) {
      const org = dataGetOrg.data;
      let hydratedType: "ORG" | "SUBORG" | null = null;
      if (org.type === OrgType.ORG || org.type === "ORG") {
        hydratedType = "ORG";
      } else if (org.type === OrgType.SUBORG || org.type === "SUBORG") {
        hydratedType = "SUBORG";
      }

      setOrganization((prev) => {
        const next = {
          ...prev,
          id: org.id || prev.id,
          name: org.name || "",
          address: org.address || "",
          city: org.city || "",
          country: org.country || "",
          state: org.state || "",
          phone: org.phone || "",
          phoneCode: org.phoneCode || org.phone_code || prev.phoneCode,
          parentId: org.parentId ?? org.parent_id ?? prev.parentId,
          type: org.type ?? prev.type,
        };

        // Seed the change baseline from the SAME object that becomes the form
        // state, rather than from a separate effect that reads state back. A
        // reader would run before these values land and capture the empty form,
        // which would make every field look changed and defeat the skip.
        savedOrgFieldsRef.current = JSON.stringify(
          orgBodyOf(next, hydratedType ?? orgTypeOfValue(next.type)),
        );

        return next;
      });

      if (hydratedType !== null) {
        setOrgType(hydratedType);
      }
      if (org.doorUnknownAsOpening !== undefined) {
        setDoorUnknownAsOpening(org.doorUnknownAsOpening);
      } else if (org.door_unknown_as_opening !== undefined) {
        setDoorUnknownAsOpening(org.door_unknown_as_opening);
      }
      if (org.hideUnconfiguredDoors !== undefined) {
        setHideUnconfiguredDoors(org.hideUnconfiguredDoors);
      } else if (org.hide_unconfigured_doors !== undefined) {
        setHideUnconfiguredDoors(org.hide_unconfigured_doors);
      }
      // Both spellings, because the endpoints that carry this field do not
      // agree on case — AND both shapes: a GET decodes the FlatBuffer
      // ordinal (a number), while other surfaces round-trip the label (a
      // string). String(2) is "2", not "ONE_CLICK", so a raw JS coercion
      // here could never match a dropdown option once the value came back
      // from the wire — the exact reason a chosen mode looked unsaved.
      // toActionFiringMode() is this app's one place that already knows
      // how to read both shapes; INHERIT (numeric 0, or the label) is an
      // organization that has never chosen, shown as unset rather than a mode.
      const loadedFiringMode = org.actionFiringMode ?? org.action_firing_mode;
      const resolvedFiringMode = toActionFiringMode(loadedFiringMode);
      setActionFiringMode(
        resolvedFiringMode === ActionFiringMode.INHERIT
          ? UNSET_FIRING_MODE
          : ActionFiringMode[resolvedFiringMode],
      );
    }
  }, [dataGetOrg]);

  // Populate holidays once fetched
  useEffect(() => {
    if (holidaysData && holidaysData.data) {
      const backendHolidays: HolidayRow[] = holidaysData.data.map((h: any) => ({
        key: h.id,
        id: h.id,
        name: h.name,
        description: h.description || "",
        day: h.day,
        month: h.month,
      }));
      setHolidays(backendHolidays);
      originalHolidayIdsRef.current = new Set(holidaysData.data.map((h: any) => h.id));
    }
  }, [holidaysData]);

  // ************************************************ */
  // HOLIDAY TABLE COLUMNS ************************* */
  const holidayColumns = [
    {
      title: t("general.name"),
      dataIndex: "name",
      key: "name",
      render: (text: string, record: HolidayRow) => (
        <Input
          size="small"
          value={text}
          placeholder="Holiday name"
          onChange={(e) => updateHoliday(record.key, "name", e.target.value)}
        />
      ),
    },
    {
      title: t("general.description"),
      dataIndex: "description",
      key: "description",
      render: (text: string, record: HolidayRow) => (
        <Input
          size="small"
          value={text}
          placeholder="Description"
          onChange={(e) => updateHoliday(record.key, "description", e.target.value)}
        />
      ),
    },
    {
      title: "Day",
      dataIndex: "day",
      key: "day",
      width: 80,
      render: (value: number, record: HolidayRow) => (
        <InputNumber
          size="small"
          min={1}
          max={31}
          value={value}
          onChange={(v) => updateHoliday(record.key, "day", v || 1)}
        />
      ),
    },
    {
      title: "Month",
      dataIndex: "month",
      key: "month",
      width: 80,
      render: (value: number, record: HolidayRow) => (
        <InputNumber
          size="small"
          min={1}
          max={12}
          value={value}
          onChange={(v) => updateHoliday(record.key, "month", v || 1)}
        />
      ),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: any, record: HolidayRow) => (
        <Button
          type="text"
          danger
          size="small"
          icon={<FontAwesomeIcon icon={faTrash} />}
          onClick={() => removeHoliday(record.key)}
        />
      ),
    },
  ];

  // ************************************************ */
  // COMPONENT ************************************* */
  return (
    <Spin spinning={isLoadingPost || isLoadingGetOrg || isLoadingDelete || saving}>
      <div style={{ padding: "16px" }}>
        {stepError && (
          <Alert
            type="error"
            showIcon
            closable
            message={stepError}
            onClose={() => setStepError(null)}
            style={{ marginBottom: 16 }}
          />
        )}
        {isErrorPost && (
          <Alert
            type="error"
            showIcon
            message="Failed to create organization. Please try again."
            style={{ marginBottom: 16 }}
          />
        )}
        {/* ORGANIZATION INFO */}
        <DrawerRowDivider icon={faBuilding} title={t("general.organization")} />
        <div className={`drawer__box__${theme}`}>
          {wizardState.organizationId && (
            <div className="container" style={{ marginBottom: "15px" }}>
              <div className="row">
                <div className="col-12 col-md-12">
                  <div style={{ display: "flex", gap: "8px", alignItems: "flex-end" }}>
                    <div style={{ flex: 1 }}>
                      <FormInputText
                        inputValue={formatUuidT(organization.id)}
                        inputLabelName="ID"
                        inputOnChange={onChangeFormValue}
                        inputVariableName="id"
                        isRequired
                        disabledInput
                        formularyType={FormularyType.WIZARD}
                      />
                    </div>
                    <Button
                      type="default"
                      size="middle"
                      icon={<FontAwesomeIcon icon={faCopy} />}
                      onClick={() => {
                        const idString = formatUuidT(organization.id);
                        navigator.clipboard.writeText(idString);
                        message.success("Organization ID copied to clipboard");
                      }}
                      style={{ marginBottom: "4px" }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-4">
                <FormInputText
                  inputValue={String(organization.name ?? "")}
                  inputLabelName={t("general.name")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-4">
                <FormInputText
                  inputValue={String(organization.address ?? "")}
                  inputLabelName={t("general.address")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="address"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-4">
                <FormInputPhoneNumber
                  inputValue={String(organization.phone ?? "")}
                  inputLabelName={t("general.phone")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="phone"
                  isRequired
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-4 mt-3">
                <FormInputSelectCountry
                  inputValue={String(organization.country ?? "")}
                  inputLabelName={t("general.country")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="country"
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <FormInputSelectState
                  inputValue={String(organization.state ?? "")}
                  inputLabelName={t("general.state")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="state"
                  country={String(organization.country ?? "")}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
              <div className="col-12 col-md-4 mt-3">
                <FormInputSelectCity
                  inputValue={String(organization.city ?? "")}
                  inputLabelName={t("general.city")}
                  inputOnChange={onChangeFormValue}
                  inputVariableName="city"
                  country={String(organization.country ?? "")}
                  state={String(organization.state ?? "")}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            </div>
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">
                  <span className="generalStyles__inputFlexRequired">*</span>
                  {t("general.type")}
                </span>
                <div style={{ marginTop: 8 }}>
                  <Radio.Group value={orgType} onChange={(e) => setOrgType(e.target.value)}>
                    <Radio value="ORG">ORG</Radio>
                    <Radio value="SUBORG">SUBORG</Radio>
                  </Radio.Group>
                </div>
              </div>
            </div>
            {orgType === "SUBORG" && (
              <div className="row">
                <div className="col-12 mt-3">
                  <span className="generalStyles__inputLabel">
                    <span className="generalStyles__inputFlexRequired">*</span>
                    {t("general.parentOrganization")}
                  </span>
                  <div style={{ marginTop: 8 }}>
                    <SelectOrganizationsLight
                      value={
                        typeof organization.parentId === "string"
                          ? organization.parentId
                          : formatUuidT(organization.parentId)
                      }
                      onChangeIdOrganization={onChangeParentOrg}
                      disabledInput={false}
                      filterType="ORG"
                      excludeIds={parentExcludeIds}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <br />
        {/* SETTINGS */}
        <DrawerRowDivider icon={faGears} title={t("general.settings")} />
        <div className={`drawer__box__${theme}`} style={{ paddingBottom: "25px" }}>
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-6 mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch
                    checked={doorUnknownAsOpening}
                    onChange={(checked) => setDoorUnknownAsOpening(checked)}
                  />
                  <span>Door Unknown as Opening</span>
                </div>
              </div>
              <div className="col-12 col-md-6 mt-3">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Switch
                    checked={hideUnconfiguredDoors}
                    onChange={(checked) => setHideUnconfiguredDoors(checked)}
                  />
                  <span>Hide Unconfigured Doors</span>
                </div>
              </div>
              <div className="col-12 col-md-6 mt-3">
                <span className="generalStyles__inputLabel">Action Firing</span>
                <Select
                  style={{ width: "100%", marginTop: 4 }}
                  value={actionFiringMode}
                  onChange={(value) => setActionFiringMode(value)}
                  data-doc-label="Action Firing"
                  data-testid="org-action-firing-mode"
                  options={[
                    {
                      value: UNSET_FIRING_MODE,
                      label: "Not set — each screen keeps its own default",
                    },
                    {
                      value: "TWO_STEP",
                      label: "Two-step — pick a floor, then press the command",
                    },
                    { value: "ONE_CLICK", label: "One-click — the floor button sends" },
                  ]}
                />
                {/* Says what the choice DOES rather than restating its name.
                    The setting decides whether a floor button dispatches an
                    elevator command on the first press, which is not something
                    an operator should have to infer from two words. */}
                <span className="generalStyles__infoText">
                  Whether picking a floor sends the command immediately, or stages it for a second
                  press. Applies to the Car Calls pad and to Actions cards. Leave unset to keep each
                  screen behaving as it does today.
                </span>
              </div>
            </div>
          </div>
        </div>
        <br />
        {/* HOLIDAYS */}
        <DrawerRowDivider icon={faCalendar} title="Holidays" />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="col-12 col-md-12 mt-2 generalStyles__flex">
                <div className="generalStyles__width100">
                  <span className="generalStyles__inputLabel">Add Common Holiday</span>
                  <Select
                    style={{ width: "100%", marginTop: 4 }}
                    placeholder="Select a common holiday..."
                    value={undefined}
                    onSelect={(value) => addCommonHoliday(value as unknown as number)}
                    options={COMMON_HOLIDAYS.map((h, i) => ({
                      label: `${h.name} (${h.month}/${h.day})`,
                      value: i,
                    }))}
                    size="large"
                  />
                </div>
                <div style={{ marginTop: "26px", marginLeft: "10px" }}>
                  <Button size="large" onClick={addAllCommonHolidays}>
                    Add All Common Holidays
                  </Button>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-12 mt-2">
                <Table
                  dataSource={holidays}
                  columns={holidayColumns}
                  pagination={false}
                  size="small"
                  locale={{ emptyText: "No holidays added" }}
                />
                <Button type="dashed" onClick={addHoliday} style={{ marginTop: 8 }} block>
                  + Add Holiday
                </Button>
              </div>
            </div>
          </div>
        </div>
        {/* SAVE / DELETE BUTTONS */}
        <Divider />
        <div className={`drawer__box__${theme}`}>
          <div className="container">
            <div className="row">
              <div className="generalStyles__flexCenter">
                <div className="generalStyles__mrFix">
                  <WizardExportButton stage={0} orgId={wizardState.organizationId} />
                </div>

                <div className="generalStyles__mrFix">
                  <Button
                    type="primary"
                    onClick={isEditMode ? handleUpdate : handleSave}
                    disabled={!isFormValid() || (isEditMode ? !access.update : !access.create)}
                    loading={isLoadingPost || saving}
                    size="large"
                    style={{ minWidth: "180px" }}
                  >
                    {isEditMode ? "Update Organization" : "Save Organization"}
                  </Button>
                </div>
                <div className="generalStyles__mrFix">
                  {wizardState.organizationId && (
                    <WizardOpGate op="delete">
                      <Popconfirm
                        title="Delete this organization?"
                        onConfirm={handleDeleteOrganization}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button
                          danger
                          loading={isLoadingDelete}
                          size="large"
                          style={{ minWidth: "180px" }}
                        >
                          Delete Organization
                        </Button>
                      </Popconfirm>
                    </WizardOpGate>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ALERTS */}
        <GlobalAlert2
          isError={isErrorPost}
          isSuccess={isSuccessPost}
          requestType="POST"
          error={errorPost}
          name="Organization"
        />
        <GlobalAlert2
          isError={isErrorPut}
          isSuccess={false}
          requestType="PUT"
          error={errorPut}
          name="Organization"
        />
        <GlobalAlert2
          isError={isErrorPatch}
          isSuccess={false}
          requestType="PUT"
          error={errorPatch}
          name="Organization Settings"
        />
      </div>
    </Spin>
  );
}
